/**
 * autoScanOnOpen — runs the Auto Scan album sweep by itself when Nexus Dex
 * opens or comes back to the foreground (2026-09-19 owner ask).
 *
 * The sweep itself is autoScanRunner.startAutoScan, unchanged: same
 * classifier, same save path, same global progress bar. This module only
 * decides WHEN to call it without a tap, and it is deliberately timid:
 *
 *   - only when the Settings switch is on (autoScanOnOpenPref);
 *   - only on a build that has the Screenshots-album plugin (iOS);
 *   - only when Photos access is ALREADY granted or limited. A launch never
 *     prompts (peekScreenshotAccess reads the grant, it never asks);
 *   - at most once per MIN_GAP_MS, so tabbing in and out of GO to check a
 *     catch does not put an album enumeration behind every foreground flip;
 *   - quietly: no banner unless there are new GO screenshots to save.
 *
 * Foreground detection mirrors appHeartbeat: Capacitor's appStateChange is
 * the reliable signal on device (visibilitychange misses in WKWebView), and
 * visibilitychange is the web/test fallback. Both are wired; the throttle
 * makes a double fire harmless.
 */
import { startAutoScan } from "./autoScanRunner";
import { isAutoScanOnOpenEnabled } from "./autoScanOnOpenPref";
import { isScreenshotSweepAvailable, peekScreenshotAccess } from "./screenshotLibrary";

/** Last time an on-open sweep actually started (ms epoch), per device. */
export const AUTO_SCAN_ON_OPEN_LAST_KEY = "nexus.autoScanOnOpen.lastAt.v1";

/**
 * Minimum gap between on-open sweeps. Five minutes: a trainer screenshotting
 * a Pokémon and switching back within that window still gets it on the next
 * open, and a busy afternoon of app-switching does not enumerate the album
 * thirty times.
 */
export const AUTO_SCAN_ON_OPEN_MIN_GAP_MS = 5 * 60 * 1000;

function lastRunAt(): number {
  try {
    if (typeof localStorage === "undefined") return 0;
    const n = Number.parseInt(localStorage.getItem(AUTO_SCAN_ON_OPEN_LAST_KEY) ?? "", 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function stampRun(now: number): void {
  try {
    localStorage.setItem(AUTO_SCAN_ON_OPEN_LAST_KEY, String(now));
  } catch {
    /* quota: worst case we sweep again next foreground */
  }
}

/** Throttle gate. Exported for tests; callers go through runAutoScanOnOpen. */
export function shouldRunAutoScanOnOpen(now: number = Date.now()): boolean {
  return now - lastRunAt() >= AUTO_SCAN_ON_OPEN_MIN_GAP_MS;
}

export type AutoScanOnOpenOutcome =
  | "started"
  | "off"
  | "unavailable"
  | "throttled"
  | "no-access"
  | "busy";

/**
 * Run one quiet sweep if every gate allows it. Resolves the reason when it
 * does not, which is what the tests pin. Never rejects.
 */
export async function runAutoScanOnOpen(
  isPro: () => boolean,
  now: number = Date.now(),
): Promise<AutoScanOnOpenOutcome> {
  if (!isAutoScanOnOpenEnabled()) return "off";
  if (!isScreenshotSweepAvailable()) return "unavailable";
  if (!shouldRunAutoScanOnOpen(now)) return "throttled";
  const status = await peekScreenshotAccess();
  if (status !== "granted" && status !== "limited") return "no-access";
  // Stamp BEFORE the sweep: a run that gets interrupted by iOS suspending
  // the webview has still advanced the cursor per item and must not restart
  // the moment the app is back, ahead of the trainer's own taps.
  stampRun(now);
  try {
    const started = await startAutoScan({ isPro: isPro(), quiet: true });
    return started ? "started" : "busy";
  } catch {
    return "busy";
  }
}

/**
 * Sweep now, and on every return to the foreground. Returns a teardown.
 */
export function startAutoScanOnOpen(isPro: () => boolean): () => void {
  const run = () => {
    void runAutoScanOnOpen(isPro).catch(() => {
      /* never surfaces */
    });
  };

  run();

  let removeNative: (() => void) | undefined;
  void (async () => {
    try {
      const { App } = await import("@capacitor/app" as any);
      const handle = await App.addListener("appStateChange", (s: { isActive: boolean }) => {
        if (s?.isActive) run();
      });
      removeNative = () => {
        void handle.remove();
      };
    } catch {
      /* not a Capacitor runtime; visibilitychange below covers it */
    }
  })();

  if (typeof document === "undefined") return () => removeNative?.();
  const onVisible = () => {
    if (document.visibilityState === "visible") run();
  };
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    removeNative?.();
  };
}

/** Test seam. */
export function __resetAutoScanOnOpenForTest(): void {
  try {
    localStorage.removeItem(AUTO_SCAN_ON_OPEN_LAST_KEY);
  } catch {
    /* ignore */
  }
}
